import { LiveToolbar } from "@faire/prototype-playground/platform/components/toolbar/LiveToolbar";
import { usePlaygroundLocalMsg } from "@faire/prototype-playground/platform/localization";
import type { PrototypeMetadata } from "@faire/prototype-playground/workspace/types";
import { useEffect, useMemo, useState } from "react";

import {
  buildFlow,
  buildForm,
  getBusinessTypeDetails,
  resolveScenarioIdFromEmail,
  SCENARIO_ORDER,
  SCENARIOS,
  STEP_LABELS,
  type BusinessPlatform,
  type PlacesSuggestion,
  type ScenarioId,
  type SignupForm,
} from "./data";
import {
  BusinessNameStep,
  BusinessTypeStep,
  type ConfirmedStoreDetails,
  ConfirmStoreStep,
  DoneStep,
  EmailModalStep,
  OpeningBudgetStep,
  OpeningSoonDateStep,
  OpeningYearStep,
  PersonalInfoStep,
  type PostalCodeMode,
  SearchingStep,
} from "./steps";

export const prototypeMeta: PrototypeMetadata = {
  name: "Magic Signup from Email Domain",
  owner: "Tony Chen",
  email: "tonychen@faire.com",
  status: "in-progress",
  platform: "web",
  createdAt: "2026-08-03",
  lastUpdated: "2026-08-03",
  surfaceArea: "Retailer",
  category: "experimental",
  creationTool: "cursor",
  thumbnail: "/images/thumbnails/tc-magic-signup.png",
  description:
    "Retailer signup with factual store matching from the email domain (PR-5154). Business type comes before the lookup beat. A Places or website-only result lands on 'We found your store'; accepting it skips business name, while declining follows the standard flow. Opening Soon selections follow the production date and brick-and-mortar budget questions. Mock-only.",
};

// Roughly the p50 lookup in production (p99 runs to 7–8s). The same wait runs
// whether or not the scrape finds anything.
const SEARCH_DURATION_MS = 4000;

export default function MagicSignupFromEmailDomainPage() {
  const [scenarioId, setScenarioId] = useState<ScenarioId>("business-email");
  const [stepIndex, setStepIndex] = useState(0);
  // Whether the retailer confirmed the inferred store identity.
  const [useMatch, setUseMatch] = useState(true);
  const [form, setForm] = useState<SignupForm>(() =>
    buildForm(SCENARIOS["business-email"], true)
  );

  const scenario = SCENARIOS[scenarioId];
  const flow = useMemo(
    () =>
      buildFlow(scenario, useMatch, form.openingYear, form.businessPlatforms),
    [scenario, useMatch, form.openingYear, form.businessPlatforms]
  );
  const step = flow[Math.min(stepIndex, flow.length - 1)];
  const progressPercent = Math.round(
    (Math.min(stepIndex, flow.length - 1) / (flow.length - 1)) * 100
  );

  useEffect(() => {
    document.title = prototypeMeta.name;
  }, []);

  // Reset the flow whenever the reviewer switches scenarios from the toolbar.
  const resetToScenario = (nextId: ScenarioId) => {
    setScenarioId(nextId);
    setStepIndex(0);
    setUseMatch(true);
    setForm(buildForm(SCENARIOS[nextId], true));
  };

  const goToStep = (nextIndex: number) => {
    setStepIndex(Math.max(0, Math.min(nextIndex, flow.length - 1)));
  };

  const handleNext = () => goToStep(stepIndex + 1);

  // The typed email is the trigger: it decides which profile (if any) we
  // resolve, so submitting the modal picks the scenario rather than just
  // advancing a screen.
  const handleEmailSubmit = (email: string) => {
    const nextId = resolveScenarioIdFromEmail(email);
    setScenarioId(nextId);
    setUseMatch(true);
    setForm(buildForm(SCENARIOS[nextId], true));
    setStepIndex(1);
  };

  // Step back one screen, skipping the transitional searching screen so the
  // reviewer lands on an interactive step.
  const handleBack = () => {
    let prev = stepIndex - 1;
    if (flow[prev] === "searching") {
      prev -= 1;
    }
    goToStep(prev);
  };

  // "Yes" keeps factual identity fields only; "No" clears the profile and
  // continues through the standard questions.
  const handleConfirmStore = (
    isMine: boolean,
    confirmedStoreDetails?: ConfirmedStoreDetails
  ) => {
    setUseMatch(isMine);
    setForm((previousForm) => {
      const nextForm = buildForm(scenario, isMine);
      return {
        ...nextForm,
        businessPlatforms: previousForm.businessPlatforms,
        openingYear: previousForm.openingYear,
        openingSoonDate: previousForm.openingSoonDate,
        openingBudget: previousForm.openingBudget,
        ...(confirmedStoreDetails ?? {}),
        city: isMine ? previousForm.city || nextForm.city : "",
        state: isMine ? previousForm.state || nextForm.state : "",
      };
    });
    handleNext();
  };

  const handleChangeField = (field: keyof SignupForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Multi-select, matching production's business-type step.
  const handleToggleBusinessPlatform = (platform: BusinessPlatform) => {
    setForm((prev) => {
      const isSelected = prev.businessPlatforms.includes(platform);
      return {
        ...prev,
        businessPlatforms: isSelected
          ? prev.businessPlatforms.filter((value) => value !== platform)
          : [...prev.businessPlatforms, platform],
      };
    });
  };

  // Picking a Places result fills the name and address and clears the manual
  // flag, so the postal-code field stays collapsed.
  const handleSelectSuggestion = (suggestion: PlacesSuggestion) => {
    setForm((prev) => ({
      ...prev,
      storeName: suggestion.name,
      postalCode: suggestion.postalCode,
      isManualBusinessName: false,
    }));
  };

  const handleEnterManually = () => {
    setForm((prev) => ({ ...prev, isManualBusinessName: true }));
  };

  // Auto-advance off the transitional searching screen.
  useEffect(() => {
    if (step !== "searching") {
      return;
    }
    const timer = setTimeout(() => {
      setStepIndex((prev) => (flow[prev] === "searching" ? prev + 1 : prev));
    }, SEARCH_DURATION_MS);
    return () => clearTimeout(timer);
  }, [step, flow]);

  // Business type runs before the card, so we already know whether a
  // website-only match owes us a ZIP — it's never asked speculatively.
  const { hasPhysicalStore } = getBusinessTypeDetails(form.businessPlatforms);
  const postalCodeMode: PostalCodeMode = hasPhysicalStore
    ? "required"
    : "hidden";
  const businessTypeTitle = usePlaygroundLocalMsg(
    "Hi {firstName}, which best describes your business?",
    { firstName: scenario.firstName }
  );
  const confirmTitle = usePlaygroundLocalMsg("We found your store");

  const renderStep = () => {
    switch (step) {
      case "email":
        return (
          <EmailModalStep
            key={scenario.id}
            scenario={scenario}
            onSubmit={handleEmailSubmit}
          />
        );
      case "personal":
        return (
          <PersonalInfoStep
            key={scenario.id}
            scenario={scenario}
            progressPercent={progressPercent}
            onBack={handleBack}
            onNext={handleNext}
          />
        );
      case "searching":
        return <SearchingStep />;
      case "confirm-store":
        return (
          <ConfirmStoreStep
            scenario={scenario}
            title={confirmTitle}
            progressPercent={progressPercent}
            postalCodeMode={postalCodeMode}
            onBack={handleBack}
            onConfirm={handleConfirmStore}
          />
        );
      case "opening-year":
        return (
          <OpeningYearStep
            form={form}
            progressPercent={progressPercent}
            showDeclinedMatchMessage={!useMatch && scenario.match !== null}
            onBack={handleBack}
            onChange={(value) => handleChangeField("openingYear", value)}
            onNext={handleNext}
          />
        );
      case "business-type":
        return (
          <BusinessTypeStep
            form={form}
            title={businessTypeTitle}
            progressPercent={progressPercent}
            onBack={handleBack}
            onToggle={handleToggleBusinessPlatform}
            onNext={handleNext}
          />
        );
      case "opening-soon-date":
        return (
          <OpeningSoonDateStep
            form={form}
            progressPercent={progressPercent}
            onBack={handleBack}
            onSelect={(value) => {
              handleChangeField("openingSoonDate", value);
              handleNext();
            }}
          />
        );
      case "opening-budget":
        return (
          <OpeningBudgetStep
            form={form}
            progressPercent={progressPercent}
            onBack={handleBack}
            onSelect={(value) => {
              handleChangeField("openingBudget", value);
              handleNext();
            }}
          />
        );
      case "business-name":
        return (
          <BusinessNameStep
            form={form}
            isOpeningSoon={form.openingYear === "Opening soon"}
            showDeclinedMatchMessage={false}
            progressPercent={progressPercent}
            onBack={handleBack}
            onChangeName={(value) => handleChangeField("storeName", value)}
            onChangePostalCode={(value) =>
              handleChangeField("postalCode", value)
            }
            onSelectSuggestion={handleSelectSuggestion}
            onEnterManually={handleEnterManually}
            onNext={handleNext}
          />
        );
      case "done":
        return (
          <DoneStep
            scenario={scenario}
            progressPercent={progressPercent}
            onRestart={() => goToStep(0)}
          />
        );
    }
  };

  return (
    <>
      <LiveToolbar
        stateGroups={[
          {
            title: "Scenario",
            queryKey: "scenario",
            description:
              "What the lookup resolves from the retailer's email domain: a Google Places listing, a website with no listing, or nothing. Any business domain still gets the loading beat; a consumer mailbox is never scraped, so it skips it.",
            states: SCENARIO_ORDER.map((id, index) => ({
              label: SCENARIOS[id].label,
              stateNumber: index,
            })),
            active: SCENARIO_ORDER.indexOf(scenarioId),
            onChange: (next) => resetToScenario(SCENARIO_ORDER[next]),
            dividerAfter: true,
          },
          {
            title: "Jump to step",
            queryKey: "step",
            description:
              "Preview any screen in the current scenario's flow without clicking through.",
            sliderMode: true,
            states: flow.map((stepId, index) => ({
              label: STEP_LABELS[stepId],
              stateNumber: index,
            })),
            active: Math.min(stepIndex, flow.length - 1),
            onChange: (next) => goToStep(next),
          },
        ]}
      />
      {renderStep()}
    </>
  );
}
